import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Avatar,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  ArrowForward as ArrowForwardIcon,
  FilterList as FilterListIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';

import workflowService from '../../../services/workflowService';

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const AdminWorkflowsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const { user } = useAuth();

  // Determine if we're in staff context
  const isStaffPortal = location.pathname.startsWith('/staff/') || user?.role === 'staff';

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Pagination and filtering
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    completed: 0
  });

  useEffect(() => {
    fetchWorkflowRequests();
  }, [page, statusFilter]);

  const fetchWorkflowRequests = async () => {
    setLoading(true);
    setError(''); // Clear previous errors
    
    try {
      let response;
      
      // Simplify the status parameter handling
      const effectiveStatus = statusFilter === 'all' ? '' : statusFilter;
      const effectiveSearch = searchTerm || '';
      
      try {
        // Use the appropriate endpoint based on user role
        if (isStaffPortal) {
          console.log('Fetching workflow requests using staff endpoint');
          response = await workflowService.getStaffRequests(page, limit, effectiveStatus, effectiveSearch);
        } else {
          console.log('Fetching workflow requests using admin endpoint');
          response = await workflowService.getAllRequests(page, limit, effectiveStatus, effectiveSearch);
        }
        
        // Handle the response data
        if (response && response.data) {
          setRequests(response.data || []);
          setTotalPages(response.totalPages || 1);
          setTotalItems(response.total || 0);
        } else {
          // Handle unexpected response format
          console.error('Unexpected response format:', response);
          setRequests([]);
          setTotalPages(1);
          setTotalItems(0);
        }
      } catch (err) {
        console.error('Error fetching workflow requests:', err);
        
        // Special handling for 403 errors
        if (err.response && err.response.status === 403) {
          console.log('Permission denied - trying alternative endpoint');
          // If admin endpoint fails with 403, try the staff endpoint as fallback
          try {
            response = await workflowService.getStaffRequests(page, limit, effectiveStatus, effectiveSearch);
            
            if (response && response.data) {
              setRequests(response.data || []);
              setTotalPages(response.totalPages || 1);
              setTotalItems(response.total || 0);
            }
          } catch (fallbackErr) {
            console.error('Fallback request also failed:', fallbackErr);
            setError('You do not have permission to view these workflow requests.');
            setRequests([]);
            setTotalPages(1);
            setTotalItems(0);
          }
        } else {
          setError(err.message || 'Failed to load workflow requests');
          setRequests([]);
          setTotalPages(1);
          setTotalItems(0);
        }
      }
      
      // Fetch counts in a separate try/catch to isolate errors
      try {
        const countsResponse = await workflowService.getRequestCounts();
        const countsData = countsResponse?.data || countsResponse;
        
        setStats({
          total: countsData?.total || 0,
          inProgress: countsData?.active || 0,
          completed: countsData?.completed || 0
        });
      } catch (countErr) {
        console.error('Error fetching request counts:', countErr);
        // Keep existing stats on error
      }
    } catch (error) {
      console.error('Unexpected error in workflow request process:', error);
      setError('An unexpected error occurred while loading workflow data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchWorkflowRequests();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchWorkflowRequests();
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handlePageChange = (e, value) => {
    setPage(value);
  };

  const handleViewTemplates = () => {
    // Navigate to the appropriate templates page based on context
    navigate(isStaffPortal ? '/staff/workflows/templates' : '/admin/workflows/templates');
  };

  const handleViewRequest = (requestId) => {
    // Navigate to the appropriate detail page based on context
    navigate(isStaffPortal ? `/staff/workflows/${requestId}` : `/admin/workflows/${requestId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h4" component="h1">
          Workflow Requests
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Add a button to navigate to the dashboard */}
          <Button
            variant="outlined"
            color="primary"
            startIcon={<DashboardIcon />}
            onClick={() => navigate(isStaffPortal ? '/staff/workflow-dashboard' : '/admin/workflow-dashboard')}
            sx={{ borderRadius: 1 }}
          >
            View Dashboard
          </Button>
          
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ borderRadius: 1 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ backgroundColor: alpha(theme.palette.primary.light, 0.04), minHeight: '100vh' }}>
        <Card 
          elevation={0} 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            p: 2,
            backgroundImage: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TimelineIcon sx={{ mr: 1 }} />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Workflow Management
            </Typography>
          </Box>
          <Typography variant="body2">
            Review and manage workflow requests from members
          </Typography>
        </Card>
        
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  Total Requests
                </Typography>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <TimelineIcon />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ mt: 2, fontWeight: 'bold' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                All workflow requests
              </Typography>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  In Progress
                </Typography>
                <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
                  <AccessTimeIcon />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ mt: 2, fontWeight: 'bold', color: theme.palette.warning.main }}>
                {stats.inProgress}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Active workflow requests
              </Typography>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  Completed
                </Typography>
                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                  <ArrowForwardIcon />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ mt: 2, fontWeight: 'bold', color: theme.palette.success.main }}>
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Completed workflow requests
              </Typography>
            </Card>
          </Grid>
        </Grid>
        
        {/* Main Content Card */}
        <Card 
          sx={{ 
            mb: 3, 
            p: 2, 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Workflow Requests
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh data">
                <IconButton onClick={handleRefresh} color="primary" sx={{ 
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SettingsIcon />}
                onClick={handleViewTemplates}
                sx={{ borderRadius: 1 }}
              >
                Manage Templates
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {/* Filters and Search */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2, 
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            mb: 3 
          }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', flexGrow: 1, maxWidth: '350px' }}>
              <TextField
                placeholder="Search by title or member..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton type="submit" edge="end" size="small">
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 1 }
                }}
              />
            </form>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="status-filter-label">Filter by Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Filter by Status"
                onChange={handleStatusFilterChange}
                sx={{ borderRadius: 1 }}
              >
                <MenuItem value="all">All Requests</MenuItem>
                <MenuItem value="Submitted">New Requests</MenuItem>
                <MenuItem value="InProgress">In Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 1 }}>
              <Typography color="error">{error}</Typography>
            </Paper>
          ) : requests.length === 0 ? (
            <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
              <TimelineIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No workflow requests found
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                There are no workflow requests matching your criteria.
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />} 
                onClick={handleRefresh}
                sx={{ mt: 1 }}
              >
                Refresh
              </Button>
            </Paper>
          ) : (
            <>
              <TableContainer sx={{ 
                boxShadow: 'none', 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                mb: 2,
                maxHeight: 600,
                overflowY: 'auto'
              }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Member</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Workflow Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Submitted</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow 
                        key={request._id} 
                        hover 
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                        }}
                        onClick={() => handleViewRequest(request._id)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {request.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              sx={{ 
                                width: 30, 
                                height: 30, 
                                mr: 1,
                                bgcolor: theme.palette.primary.main,
                                fontSize: '0.875rem'
                              }}
                            >
                              {request.member?.userId?.firstName?.charAt(0) || 'M'}
                            </Avatar>
                            <Typography variant="body2">
                              {request.member?.userId?.firstName} {request.member?.userId?.lastName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {request.workflowTemplate?.name || 'Custom Workflow'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={request.currentStatus || 'Submitted'} 
                            size="small"
                            color={
                              request.isCompleted ? 'success' : 
                              request.currentStatus === 'Submitted' ? 'info' : 'warning'
                            }
                            sx={{ 
                              fontWeight: 500,
                              minWidth: 90,
                              textAlign: 'center'
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(request.createdAt)}</TableCell>
                        <TableCell>{formatDate(request.updatedAt)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="View details">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRequest(request._id);
                              }}
                              sx={{ 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                                }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 2,
                px: 1
              }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {requests.length} of {totalItems} requests
                </Typography>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary"
                  size="medium"
                  shape="rounded"
                />
              </Box>
            </>
          )}
        </Card>
      </Box>
    </Container>
  );
};

export default AdminWorkflowsPage;
